import { useFlags } from 'launchdarkly-react-client-sdk';

import { useDerivedNetworkContext } from '@/context';
import { useReleaseFlag } from '@/hooks';
import { useFileFetcher } from '@/services/queryUtils';
import type { DateRange, ShowFilter } from '@/types';
import { downloadFile } from '@/utils/dataDownloadUtils';

interface DeviceDownloadsExportResult {
  warningMessage?: string;
}

/**
 * Calls the server-side device downloads export endpoint and triggers a file download.
 * Returns a ZIP containing a table CSV (device, downloads, % of total) and a graph CSV
 * (dates × devices).
 */
const useDeviceDownloadsCsvExport = () => {
  const fetchFile = useFileFetcher();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const flag = useReleaseFlag('audience_page_backfill');

  const handleDeviceDownloadsCSVExport = async (
    dateRange: DateRange | undefined,
    selectedShow: ShowFilter,
    granularity: string
  ): Promise<DeviceDownloadsExportResult | void> => {
    if (!dateRange) return undefined;

    const queryParams = new URLSearchParams({
      from: dateRange.from,
      to: dateRange.to,
      granularity,
    });
    if (selectedShow && selectedShow !== 'all') {
      queryParams.append('showIds', selectedShow);
    }
    sources.forEach((source) => queryParams.append('sources', source));
    if (flag) {
      queryParams.append('flag', flag);
    }

    const path = `/v2/networks/${selectedNetwork.id}/audience/device-downloads/export?${queryParams.toString()}`;
    const { blob, fileName, warningMessage } = await fetchFile(path);

    downloadFile(blob, fileName);
    return { warningMessage };
  };

  return { handleDeviceDownloadsCSVExport };
};

export default useDeviceDownloadsCsvExport;
